import {
  PaymentStatus,
  useGetAllTotPermitTransactionsQuery,
} from '@/api/ariadne/__generated_types__/apollo';
import * as userSelectors from '@/user/user-selectors';
import {
  IServerSideDatasource,
  SortChangedEvent,
} from '@ag-grid-community/core';
import { AgGridReact as AgGridReactType } from '@ag-grid-community/react/lib/agGridReact';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { uiSortModeToAriadneSortMode } from '../constants';
import { PaymentPeriodDateRange, SortMode, TopLevelTOTFilters } from '../types';
import { sortModelToMode } from '../utils/sort-model-to-mode';
import { filterFingerprint } from '../utils/top-level-tot-filter-fingerprint';
import { uiFiltersToServerFilters } from '../utils/ui-filters-to-server-filters';

interface Args {
  filters: TopLevelTOTFilters;
  gridApi: React.RefObject<AgGridReactType>;
  period: PaymentPeriodDateRange;
  searchQuery: string;
  taxStatus?: PaymentStatus;
}

type TFunction = (args: Args) => {
  serverSideDatasource: IServerSideDatasource;
  sortChangedHandler: (event: SortChangedEvent) => void;
};

export const useTOTReportingServerSideDatasource: TFunction = (args) => {
  const { gridApi, period, searchQuery, taxStatus } = args;

  /**
   * The following variables use `useRef` to prevent unintentional queries from being initiated.
   *
   * If we used useState, hooks defined later would need to respond to changes via the dependency array.
   * However, this could cause issues by triggering component re-renders or refreshing hooks, both of which
   * might occasionally result in unintended API queries.
   *
   * In this scenario, we leverage useRef, allowing hooks to access the latest query parameter values
   * by reference. This prevents unnecessary re-renders while ensuring up-to-date values without
   * triggering unintended queries to our GQL API.
   *
   * If the action originates from the table itself, it's likely a case for useRef.
   */
  const pageCursor = useRef<string | null | undefined>(null);
  const sortMode = useRef<SortMode | null>(null);

  const regionId: string = useSelector(userSelectors.currentCity);

  const filters = useMemo(
    () => uiFiltersToServerFilters(args.filters),
    [args.filters]
  );

  const filterFingerPrint = useMemo(
    () => filterFingerprint(filters),
    [filters]
  );

  const refreshServerSideStore = debounce(() => {
    if (!gridApi.current?.api) return;

    gridApi.current.api.refreshServerSideStore({ purge: true });
    gridApi.current.api.setSortModel(null);
  }, 100);

  useEffect(() => {
    pageCursor.current = undefined;
    sortMode.current = null;
    // Not included in the dependencies array, as purging is only required when the query parameters change
    refreshServerSideStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFingerPrint, period, searchQuery, taxStatus]);

  /**
   * "useQuery" with "skip=true", because we want to be able to provide "fetchMore" with a partial set of
   *  variables as "fetchMore" is included as a dependency of "serverSideDatasource".
   * If there are too many dependencies for "serverSideDatasource", then it could lead to Rentalscape
   *  spamming our GQL API with queries.
   */
  const { fetchMore } = useGetAllTotPermitTransactionsQuery({
    skip: true,
    variables: {
      /**
       * ! If needed, ensure that parameters that exist outside the scope of the AG Grid-based table
       * ! are included as dependencies of the "useEffect" that triggers "refreshServerSideStore".
       *
       * If this is not done correctly, Rentalscape may or will request the wrong set of data.
       *
       * ! If variables are based on references (useRef, etc.), ensure that they are also
       * ! included in the "serverSideDatasource" utilization of "fetchMore".
       *
       * If this is not done correctly, Rentalscape may or will request the wrong set of data.
       */
      regionId,
      filters: {
        reportingStatus: filters.reportingStatus,
        interest: filters.interest,
        penalties: filters.penalties,
        discrepancy: filters.discrepancy,
        period,
        removeZeroTaxableReceipts: filters.removeZeroTaxableReceipts ?? false,
        searchQuery,
        status: taxStatus ? [taxStatus] : [],
      },
      first: 50,
      orderBy: sortMode.current
        ? [uiSortModeToAriadneSortMode[sortMode.current]]
        : [],
      after: pageCursor.current,
    },
  });

  const serverSideDatasource: IServerSideDatasource = useMemo(
    () => ({
      /**
       * Debouncing is necessary to prevent an issue where, occasionally,
       * the first load of the AG Grid-based table triggers a double query.
       *
       * Normally, this bug manifests itself by taking the user to the second page of data
       * upon the initial load of the view if the first data fetch happened to be faster than 500ms.
       */
      getRows: debounce(async (params) => {
        const { data } = await fetchMore({
          variables: {
            // ! All variables defined here must also exist within the query hook parameters.
            orderBy: sortMode.current
              ? [uiSortModeToAriadneSortMode[sortMode.current]]
              : [],
            after: pageCursor.current,
          },
        });

        if (!data) return;
        pageCursor.current = data.region.taxItems.pageInfo.endCursor;
        const rowData = data.region.taxItems.edges.map((e) => e.node);
        const rowCount =
          data.region.taxItems.pageInfo.hasNextPage === 'true'
            ? undefined
            : params.api.paginationGetCurrentPage() *
                params.api.paginationGetPageSize() +
              rowData.length;
        params.success({ rowData, rowCount });
      }, 1), // Arbitrary number that worked.
    }),
    /**
     * ! Too many dependencies may lead to Rentalscape spamming our GQL API with queries.
     * Each instance of the AG Grid-based table re-rendering would initiate its own request for data.
     */
    [fetchMore]
  );

  /**
   * Do not add any state mutations that could cause re-renders within this useCallback,
   * as they may occasionally trigger unintentional queries to our GQL API.
   *
   * AG Grid clean up the table and request the data via serverSideDatasource defined above
   * by itself.
   */
  const sortChangedHandler = useCallback(
    (event: SortChangedEvent) => {
      pageCursor.current = undefined;
      sortMode.current = sortModelToMode(event.api.getSortModel());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { serverSideDatasource, sortChangedHandler };
};
